import type { TFunction } from 'i18next';
import type { DataGridColumnOptionsProps } from '../../components/DataGridControl/DataGridControl';
import type { DropdownOption } from '../../components/Dropdown/DropdownOption/DropdownOption';
import type { User } from '../../lib/flotiq-client/types/api-response';

import Avatar from '../../components/Avatar/Avatar';
import {
  checkboxRenderer,
  dateRenderer,
  defaultRenderer,
} from '../../components/DataGrid/DataGridCell/cellRenderer';

import { ArrowUpRightIcon } from '../../images/shapes';

export const getDefinedColumns = (
  gridOptionsByID: Record<string, DataGridColumnOptionsProps> | undefined,
  hiddenColumnsById: Record<string, boolean | undefined>,
  editGrid: boolean,
  t: TFunction,
  testId?: string,
  isAllUsers?: boolean,
  filterDatasourceFetch?: (
    accessor: string,
    query?: string,
  ) => Promise<DropdownOption[]>,
  isRoleAdmin?: boolean,
) => ({
  ...(isAllUsers && {
    id: {
      accessor: 'id',
      label: 'ID',
      width: gridOptionsByID?.['id']?.width,
      render: (data: string) => defaultRenderer(data),
      sortable: !editGrid,
      filterInputType: 'text',
      resizable: true,
      removable: true,
      hide: hiddenColumnsById.id,
    },
  }),
  email: {
    accessor: 'email',
    label: t('Global.Email'),
    width: gridOptionsByID?.['email']?.width,
    render: (data: string, rowData: User) => (
      <>
        <span className="w-fit h-fit mr-2">
          <Avatar
            userInitials={rowData.firstName[0] + rowData.lastName[0]}
            testId={testId}
          />
        </span>
        <span className="min-w-0 truncate">{data}</span>
      </>
    ),
    sortable: !editGrid,
    filterInputType: 'text',
    resizable: true,
    removable: true,
    hide: hiddenColumnsById.email,
  },
  firstName: {
    accessor: 'firstName',
    label: t('Global.FirstName'),
    width: gridOptionsByID?.['firstName']?.width,
    render: (data: string) => defaultRenderer(data),
    sortable: !editGrid,
    filterInputType: 'text',
    resizable: true,
    removable: true,
    hide: hiddenColumnsById.firstName,
  },
  lastName: {
    accessor: 'lastName',
    label: t('Global.LastName'),
    width: gridOptionsByID?.['lastName']?.width,
    render: (data: string) => defaultRenderer(data),
    sortable: !editGrid,
    filterInputType: 'text',
    resizable: true,
    removable: true,
    hide: hiddenColumnsById.lastName,
  },
  enabled: {
    accessor: 'enabled',
    label: t('Global.Status'),
    width: gridOptionsByID?.['enabled']?.width,
    render: (data: boolean) => {
      if (isAllUsers) return checkboxRenderer(data);
      return defaultRenderer(data ? t('Users.Active') : t('Users.Invited'));
    },
    sortable: !editGrid,
    filterInputType: isAllUsers ? 'checkbox' : 'select',
    filterInputOptions: isAllUsers
      ? []
      : [
          { label: t('Users.Active'), value: 'true' },
          { label: t('Users.Invited'), value: 'false' },
        ],
    resizable: true,
    removable: true,
    hide: hiddenColumnsById.enabled,
    baseWidth: isAllUsers ? 120 : 150,
  },
  spaces: {
    accessor: 'spaces',
    label: t('Global.Spaces'),
    width: gridOptionsByID?.['spaces']?.width,
    render: (data: { name: string }[]) =>
      defaultRenderer(data?.map((el) => el.name).join(', ')),
    sortable: !editGrid,
    resizable: true,
    removable: true,
    hide: hiddenColumnsById['spaces'],
    filterInputType: 'text',
    filterInputOptions: undefined,
  },
  roles: {
    accessor: 'roles',
    label: t('Users.Roles'),
    width: gridOptionsByID?.['roles']?.width,
    render: (data: string[]) =>
      defaultRenderer(
        data
          ?.map((role) =>
            role
              .replace('ROLE_HEADLESS_ADMIN', 'Organization Admin')
              .replace('ROLE_HEADLESS_USER', 'Organization User')
              .replace('ROLE_ADMIN', 'Flotiq Admin')
              .replace('ROLE_USER', 'Flotiq User'),
          )
          .join(', '),
      ),
    sortable: true,
    filterInputType: 'select',
    filterInputOptions: [
      {
        value: 'role_headless_admin',
        label: 'Organization Admin',
      },
      {
        value: 'role_headless_user',
        label: 'Organization User',
      },
      ...(isRoleAdmin
        ? [
            {
              value: 'role_admin',
              label: 'Flotiq Admin',
            },
            {
              value: 'role_user',
              label: 'Flotiq User',
            },
          ]
        : []),
    ],
    multiple: true,
    resizable: true,
    removable: true,
    hide: hiddenColumnsById['roles'],
  },
  ...(isAllUsers
    ? {
        'organization.name': {
          accessor: 'organization.name',
          label: t('Users.Organization'),
          width: gridOptionsByID?.['organization.name']?.width,
          render: (data: string) => defaultRenderer(data),
          sortable: !editGrid,
          filterInputType: 'text',
          resizable: true,
          removable: true,
          hide: hiddenColumnsById['organization.name'],
        },
        'organization.id': {
          accessor: 'organization.id',
          label: t('Users.OrganizationId'),
          width: gridOptionsByID?.['organization.id']?.width,
          render: (data: string) => defaultRenderer(data),
          sortable: !editGrid,
          filterInputType: 'text',
          resizable: true,
          removable: true,
          hide: hiddenColumnsById['organization.id'],
        },
        paymentClientId: {
          accessor: 'paymentClientId',
          label: t('Users.PaymentClientId'),
          width: gridOptionsByID?.['paymentClientId']?.width,
          render: (data: string) =>
            data
              ? defaultRenderer(
                  <>
                    <ArrowUpRightIcon className="inline-block w-3 mr-2" />
                    <span className="group-hover:underline">{data}</span>
                  </>,
                )
              : '',
          sortable: !editGrid,
          filterInputType: 'text',
          resizable: true,
          removable: true,
          hide: hiddenColumnsById['paymentClientId'],
        },
        source: {
          accessor: 'source',
          label: t('Users.Source'),
          width: gridOptionsByID?.['source']?.width,
          render: (data: string) => defaultRenderer(data),
          sortable: !editGrid,
          filterInputType: 'text',
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.source,
          baseWidth: 150,
        },
        defaultSpace: {
          accessor: 'defaultSpace',
          label: t('Users.DefaultSpace'),
          width: gridOptionsByID?.['defaultSpace']?.width,
          render: (data: boolean) => checkboxRenderer(data),
          sortable: !editGrid,
          filterInputType: 'checkbox',
          resizable: true,
          removable: true,
          hide: hiddenColumnsById['defaultSpace'],
        },
        lastLogin: {
          accessor: 'lastLogin',
          label: t('Users.LastLogin'),
          width: gridOptionsByID?.['lastLogin']?.width,
          render: (data: string) => dateRenderer(data),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.lastLogin,
        },
        createdAt: {
          accessor: 'createdAt',
          label: t('Users.Registered'),
          width: gridOptionsByID?.['createdAt']?.width,
          render: (data: string) => dateRenderer(data),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.createdAt,
        },
        companyName: {
          accessor: 'companyName',
          label: t('Users.CompanyName'),
          filterInputType: 'text',
          width: gridOptionsByID?.['companyName']?.width,
          render: (data: string) => defaultRenderer(data),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.companyName,
        },
        country: {
          accessor: 'country',
          label: t('Users.Country'),
          filterInputType: 'text',
          width: gridOptionsByID?.['country']?.width,
          render: (data: string) => defaultRenderer(data),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.country,
        },
        occupation: {
          accessor: 'occupation',
          label: t('Users.Occupation'),
          width: gridOptionsByID?.['occupation']?.width,
          render: (data: string) => defaultRenderer(data),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.occupation,
          filterInputType: 'select',
          filterInputOptions: [
            {
              value: 'developer',
              label: 'Developer',
            },
            {
              value: 'contentCreatorManager',
              label: 'Content creator manager',
            },
            {
              value: 'both',
              label: 'Both',
            },
          ],
        },
        workType: {
          accessor: 'workType',
          label: t('Users.WorkType'),
          width: gridOptionsByID?.['workType']?.width,
          render: (data: string) => defaultRenderer(data),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.workType,
          filterInputType: 'select',
          filterInputOptions: [
            {
              value: 'agency',
              label: 'Agency',
            },
            {
              value: 'businessOrOrganization',
              label: 'Business or organization',
            },
          ],
        },
        termsOfService: {
          accessor: 'termsOfService',
          label: t('Users.TermsOfService'),
          width: gridOptionsByID?.['termsOfService']?.width,
          render: (data: boolean) => checkboxRenderer(data),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.termsOfService,
          filterInputType: 'checkbox',
        },
        privacyPolicy: {
          accessor: 'privacyPolicy',
          label: t('Users.PrivacyPolicy'),
          width: gridOptionsByID?.['privacyPolicy']?.width,
          render: (data: boolean) => checkboxRenderer(data),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.privacyPolicy,
          filterInputType: 'checkbox',
        },
        agreeNewsUpdates: {
          accessor: 'agreeNewsUpdates',
          label: t('Users.AgreeNewsUpdates'),
          width: gridOptionsByID?.['agreeNewsUpdates']?.width,
          render: (data: boolean) => checkboxRenderer(data),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.agreeNewsUpdates,
          filterInputType: 'checkbox',
        },
        additionalInformationStatus: {
          accessor: 'additionalInformationStatus',
          label: t('Users.AdditionalInformationStatus'),
          width: gridOptionsByID?.['additionalInformationStatus']?.width,
          render: (data: string) => defaultRenderer(data),
          sortable: !editGrid,
          resizable: true,
          removable: true,
          hide: hiddenColumnsById.additionalInformationStatus,
          filterInputType: 'select',
          filterInputOptions: [
            {
              value: 'required',
              label: 'Required',
            },
            {
              value: 'optional',
              label: 'Optional',
            },
            {
              value: 'done',
              label: 'Done',
            },
          ],
        },
      }
    : {}),
});
